/* eslint-disable jira/filename-case */
import { type MessageDescriptor, defineMessage } from '@atlassian/jira-intl';
import { getTypedKeys } from '@atlassian/jira-shared-types/src/narrowing.tsx';

const queues = defineMessage({
	id: 'work-category.common.queues',
	defaultMessage: 'Queues',
	description: 'Title of the queues section for all tickets',
});

const serviceRequests = defineMessage({
	id: 'work-category.common.service-requests',
	defaultMessage: 'Service requests',
	description:
		'Title of queues section for tickets which fall under the ITSM practice "Service requests"',
});

const incidents = defineMessage({
	id: 'work-category.common.incidents',
	defaultMessage: 'Incidents',
	description: 'Title of queues section for tickets which fall under the ITSM practice "Incidents"',
});

const changes = defineMessage({
	id: 'work-category.common.changes',
	defaultMessage: 'Changes',
	description:
		'Title of the queues section for tickets which fall under the ITSM practice "Changes"',
});

const problems = defineMessage({
	id: 'work-category.common.problems',
	defaultMessage: 'Problems',
	description:
		'Title of the queues section for tickets which fall under the ITSM practice "Problems"',
});

const postIncidentReviews = defineMessage({
	id: 'work-category.common.post-incident-reviews',
	defaultMessage: 'Post-incident reviews',
	description:
		'Title of the queues section for tickets which fall under the ITSM practice "Post-incident reviews"',
});

const developerEscalation = defineMessage({
	id: 'work-category.common.developer-escalation',
	defaultMessage: 'Developer escalations',
	description:
		'Title of queues section for tickets which fall under the cs practice "Developer escalation"',
});

const cases = defineMessage({
	id: 'work-category.common.cases',
	defaultMessage: 'Cases',
	description: 'Title of queues section for tickets which fall under the cs practice "Cases"',
});

const noCategory = defineMessage({
	id: 'work-category.common.no-category',
	defaultMessage: 'General',
	description: 'Category for request type which does not belong to any ITSM practice category',
});

const queuesLower = defineMessage({
	id: 'work-category.common.queues-lower',
	defaultMessage: 'queues',
	description: 'Title of the queues section for all tickets',
});

const serviceRequestsLower = defineMessage({
	id: 'work-category.common.service-requests-lower',
	defaultMessage: 'service requests',
	description:
		'Title of queues section for tickets which fall under the ITSM practice "Service requests"',
});

const incidentsLower = defineMessage({
	id: 'work-category.common.incidents-lower',
	defaultMessage: 'incidents',
	description: 'Title of queues section for tickets which fall under the ITSM practice "Incidents"',
});

const changesLower = defineMessage({
	id: 'work-category.common.changes-lower',
	defaultMessage: 'changes',
	description:
		'Title of the queues section for tickets which fall under the ITSM practice "Changes"',
});

const problemsLower = defineMessage({
	id: 'work-category.common.problems-lower',
	defaultMessage: 'problems',
	description:
		'Title of the queues section for tickets which fall under the ITSM practice "Problems"',
});

const postIncidentReviewsLower = defineMessage({
	id: 'work-category.common.post-incident-reviews-lower',
	defaultMessage: 'post-incident reviews',
	description:
		'Title of the queues section for tickets which fall under the ITSM practice "Post-incident reviews"',
});

const noCategoryLower = defineMessage({
	id: 'work-category.common.no-category-lower',
	defaultMessage: 'general',
	description: 'Category for request type which does not belong to any ITSM practice category',
});

const developerEscalationLower = defineMessage({
	id: 'work-category.common.developer-escalation-lower',
	defaultMessage: 'developer escalations',
	description:
		'Title of queues section for tickets which fall under the cs practice "Developer escalation"',
});

const casesLower = defineMessage({
	id: 'work-category.common.cases-lower',
	defaultMessage: 'cases',
	description: 'Title of queues section for tickets which fall under the cs practice "Cases"',
});

/**
 * Work categories x API resource
 * all the values returned by API
 */
export const CHANGES = 'change-management' as const;
export const INCIDENTS = 'incident-management' as const;
export const PROBLEMS = 'problem-management' as const;
export const SERVICE_REQUEST = 'service-request' as const;
export const POST_INCIDENT_REVIEWS = 'post-incident-review' as const;
export const DEVELOPER_ESCALATION = 'developer-escalation' as const;
export const CASES = 'case-management' as const;

/**
 * Work categories x UI extra
 * extra entry to ensure fallback scenario
 */
export const NO_CATEGORY = 'no-category' as const;
const NO_CATEGORY_NEW = 'no-category-new' as const;
export const NO_PRACTICE = 'no-practice' as const; // BERRY-1358 should consolidate into NO_CATEGORY

/**
 * Work category x Short form
 * usually used in urls
 */
export const SHORT_FORM_CHANGES = 'changes' as const;
export const SHORT_FORM_INCIDENTS = 'incidents' as const;
export const SHORT_FORM_PROBLEMS = 'problems' as const;
export const SHORT_FORM_SERVICE_REQUEST = 'service-requests' as const;
export const SHORT_FORM_POST_INCIDENT_REVIEWS = 'post-incident-reviews' as const;
export const SHORT_FORM_DEVELOPER_ESCALATION = 'developer-escalations' as const;
export const SHORT_FORM_CASES = 'cases' as const;

export type ItsmPracticeCategory =
	| typeof CHANGES
	| typeof INCIDENTS
	| typeof PROBLEMS
	| typeof SERVICE_REQUEST
	| typeof POST_INCIDENT_REVIEWS
	| typeof CASES;

export type ItsmPractice = ItsmPracticeCategory | typeof NO_CATEGORY;

export const WORK_CATEGORIES_API_RESOURCE: (
	| 'change-management'
	| 'incident-management'
	| 'problem-management'
	| 'service-request'
	| 'post-incident-review'
	| 'developer-escalation'
	| 'case-management'
)[] = [
	CHANGES,
	INCIDENTS,
	PROBLEMS,
	SERVICE_REQUEST,
	POST_INCIDENT_REVIEWS,
	DEVELOPER_ESCALATION,
	CASES,
]; // /!\ defines WorkCategoryApiResource type

/**
 * Work categories x Queue
 * NOTE: have these in order they should appear in UI (used at src/packages/router-resources/service-desk-practices/src/controllers/index.tsx)
 */
export const WORK_CATEGORIES_QUEUE_SUPPORTED: ItsmPracticeCategory[] = [
	SERVICE_REQUEST,
	INCIDENTS,
	PROBLEMS,
	CHANGES,
	POST_INCIDENT_REVIEWS,
	CASES,
]; // /!\ defines WorkCategoryQueueSupported type

export const ALL_PRACTICES: ItsmPractice[] = [...WORK_CATEGORIES_QUEUE_SUPPORTED, NO_CATEGORY];

export const WORK_CATEGORIES_SHORT_FORM: readonly [
	'changes',
	'incidents',
	'problems',
	'service-requests',
	'post-incident-reviews',
	'developer-escalations',
	'cases',
] = [
	SHORT_FORM_CHANGES,
	SHORT_FORM_INCIDENTS,
	SHORT_FORM_PROBLEMS,
	SHORT_FORM_SERVICE_REQUEST,
	SHORT_FORM_POST_INCIDENT_REVIEWS,
	SHORT_FORM_DEVELOPER_ESCALATION,
	SHORT_FORM_CASES,
] as const; // /!\ defines WorkCategoryShortForm type

export const ItsmPracticeToShortForm: Record<ItsmPractice, string> = {
	[CHANGES]: SHORT_FORM_CHANGES,
	[INCIDENTS]: SHORT_FORM_INCIDENTS,
	[PROBLEMS]: SHORT_FORM_PROBLEMS,
	[SERVICE_REQUEST]: SHORT_FORM_SERVICE_REQUEST,
	[POST_INCIDENT_REVIEWS]: SHORT_FORM_POST_INCIDENT_REVIEWS,
	[CASES]: SHORT_FORM_CASES,
	[NO_CATEGORY]: NO_CATEGORY_NEW,
};

/**
 * Work category x messages
 */
export const PRACTICE_LABEL_MAP: {
	readonly 'change-management': {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly 'service-request': {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly 'problem-management': {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly 'incident-management': {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly 'post-incident-review': {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly 'developer-escalation': {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly 'case-management': {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly 'no-category': {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly 'no-practice': {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = {
	[CHANGES]: changes,
	[SERVICE_REQUEST]: serviceRequests,
	[PROBLEMS]: problems,
	[INCIDENTS]: incidents,
	[POST_INCIDENT_REVIEWS]: postIncidentReviews,
	[NO_CATEGORY]: queues,
	[DEVELOPER_ESCALATION]: developerEscalation,
	[CASES]: cases,
	[NO_PRACTICE]: noCategory,
} as const;

export const PRACTICE_LABEL_MAP_LOWER_CASE: {
	readonly 'change-management': {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly 'service-request': {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly 'problem-management': {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly 'incident-management': {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly 'post-incident-review': {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly 'developer-escalation': {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly 'case-management': {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly 'no-category': {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly 'no-practice': {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = {
	[CHANGES]: changesLower,
	[SERVICE_REQUEST]: serviceRequestsLower,
	[PROBLEMS]: problemsLower,
	[INCIDENTS]: incidentsLower,
	[POST_INCIDENT_REVIEWS]: postIncidentReviewsLower,
	[NO_CATEGORY]: queuesLower,
	[DEVELOPER_ESCALATION]: developerEscalationLower,
	[CASES]: casesLower,
	[NO_PRACTICE]: noCategoryLower,
} as const;

type WorkCategoryQueueSupportedLabelsType = Record<ItsmPracticeCategory, MessageDescriptor>;

export const WORK_CATEGORIES_QUEUE_SUPPORTED_LABELS: WorkCategoryQueueSupportedLabelsType =
	getTypedKeys(PRACTICE_LABEL_MAP).reduce((acc, labelMapKey) => {
		// cannot type .indexOf() as not-string in Ts, therefore need to use .find(). And shows error if `key` in acc[key] being of wider type than the result (than ItsmPracticeCategory), therefore, need to use different keys in the `acc[queueKey] =` line
		const queueKey = WORK_CATEGORIES_QUEUE_SUPPORTED.find((key) => key === labelMapKey);
		if (queueKey) {
			acc[queueKey] = PRACTICE_LABEL_MAP[labelMapKey];
		}
		return acc;
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	}, {} as WorkCategoryQueueSupportedLabelsType);

/**
 * Work category x JQL
 */
export const CHANGES_JQL_NAME = 'Changes' as const;
export const INCIDENTS_JQL_NAME = 'Incidents' as const;
const PROBLEMS_JQL_NAME = 'Problems' as const;
const SERVICE_REQUEST_JQL_NAME = 'Service requests' as const;
const POST_INCIDENT_REVIEWS_JQL_NAME = 'Post-incident reviews' as const;
export const DEVELOPER_ESCALATION_JQL_NAME = 'Developer escalation' as const;
export const CASES_JQL_NAME = 'Cases' as const;

export const practiceKeyJqlNameMapping: {
	readonly 'change-management': 'Changes';
	readonly 'incident-management': 'Incidents';
	readonly 'problem-management': 'Problems';
	readonly 'service-request': 'Service requests';
	readonly 'post-incident-review': 'Post-incident reviews';
	readonly 'developer-escalation': 'Developer escalation';
	readonly 'case-management': 'Cases';
} = {
	[CHANGES]: CHANGES_JQL_NAME,
	[INCIDENTS]: INCIDENTS_JQL_NAME,
	[PROBLEMS]: PROBLEMS_JQL_NAME,
	[SERVICE_REQUEST]: SERVICE_REQUEST_JQL_NAME,
	[POST_INCIDENT_REVIEWS]: POST_INCIDENT_REVIEWS_JQL_NAME,
	[DEVELOPER_ESCALATION]: DEVELOPER_ESCALATION_JQL_NAME,
	[CASES]: CASES_JQL_NAME,
} as const;
